import React from 'react';
import styled from '@emotion/styled';
import { ContentWrap, Css, H3, Markdown, Responsive } from '../../../../../ui';
import { ISolicitationEntry } from '../../../../../contentful/types/ISolicitationEntry';
import ContactItem from '../../../../../components/common/contact/ContactItem';
import CardsContainer from '../../../../../components/common/cards/CardsContainer';
import HASlogo from '../../../../../assets/airport-logos/has-logo-horizontal.svg';
import { MarkdownRenderContextProvider } from '../../../../../components/common/markdown';
import SolicitationForm from '../form/SolicitationForm';
import SolicitationDocuments from './SolicitationDocuments';
import { formatFullDate } from '../../../../../i18n/formatFullDate';

interface ISolicitationDetailsContent {
	solicitation: ISolicitationEntry;
}

export default function SolicitationDetailsContent({
	solicitation,
}: ISolicitationDetailsContent) {
	const {
		contactName,
		contactPhone,
		contactEmail,
		conferenceInformation,
		projectDescription,
		projectGoals,
		awardDate,
		contractAmount,
		awardedContractor,
	} = solicitation.fields;

	const showAwardSection = awardDate || contractAmount || awardedContractor;

	return (
		<StyledContentWrap>
			{contactName && contactEmail && (
				<ContentSection>
					<ContentHeader styling="h5">Contact</ContentHeader>
					<CardsContainer>
						<ContactItem
							links={{ email: contactEmail }}
							title={contactName}
							phoneNumber={contactPhone}
							imageSource={<HASlogo />}
						/>
					</CardsContainer>
				</ContentSection>
			)}

			{conferenceInformation && (
				<ContentSection>
					<ContentHeader styling="h5">Conference Information</ContentHeader>
					<MarkdownRenderContextProvider>
						<Markdown source={conferenceInformation} />
					</MarkdownRenderContextProvider>
				</ContentSection>
			)}

			{projectDescription && (
				<ContentSection>
					<ContentHeader styling="h5">Project Description</ContentHeader>
					<MarkdownRenderContextProvider>
						<Markdown source={projectDescription} />
					</MarkdownRenderContextProvider>
				</ContentSection>
			)}

			{projectGoals && (
				<ContentSection>
					<ContentHeader styling="h5">Project Goals</ContentHeader>
					<MarkdownRenderContextProvider>
						<Markdown source={projectGoals} />
					</MarkdownRenderContextProvider>
				</ContentSection>
			)}

			<ContentSection>
				<ContentHeader styling="h5">
					Join Prospective Proposers List to Receive Update Notices
				</ContentHeader>
				<SolicitationForm solicitation={solicitation} />
			</ContentSection>

			<ContentSection>
				<ContentHeader styling="h5">Documents</ContentHeader>
				<SolicitationDocuments solicitation={solicitation} />
			</ContentSection>

			{showAwardSection && (
				<ContentSection>
					<ContentHeader styling="h5">Award Information</ContentHeader>
					{awardDate && (
						<TextLine>
							<span>Award Date:</span>{' '}
							{formatFullDate(awardDate, 'MM/dd/yyyy', true)}
						</TextLine>
					)}
					<TextLine>
						<span>Contract Amount:</span> {contractAmount}
					</TextLine>
					<TextLine>
						<span>Awarded Contractor:</span> {awardedContractor}
					</TextLine>
				</ContentSection>
			)}
		</StyledContentWrap>
	);
}

const StyledContentWrap = styled(ContentWrap)`
	padding-top: ${Css.pxToRem(80)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding-top: ${Css.pxToRem(40)};
	}
`;

const ContentSection = styled.div`
	margin-bottom: ${Css.pxToRem(80)};

	span {
		font-weight: 400;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-bottom: ${Css.pxToRem(40)};
	}
`;

const TextLine = styled.p`
	margin-bottom: 0;
`;

const ContentHeader = styled(H3)`
	margin-bottom: ${Css.pxToRem(20)};
`;
